import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  getLabelsUniqueCharacters,
  getWeightString,
  IFont,
  isFontEqualTo,
  isSystemFont
} from '../../data/IFont';
import {
  generateClassName,
  isCustomFont,
  isPro,
  makeFontNameCustom
} from '../../utils';
import { AVAILABLE_FONTS } from '../../data/Config';
import { loadFonts } from '../../utils/FontLoader';
import { getStoreState, storeDispatch } from 'state/Store';
import * as Actions from 'state/actions';
import { getTemplates } from 'utils/FirebaseUtils';
import { FrameType } from 'data/FrameType';
import { TextFrameData } from 'data/FlyerData';
import styled from 'styled-components';
import { trackEvent } from 'utils/Tracker';

const UpgradeBox = styled.div`
  font-weight: 400;
  font-size: 12px;
  text-align: center;
  color: var(--secondary-text-color);
  padding: 8px;
`;

interface OptionProps {
  value: IFont;
  selected: boolean;
  onChange: (value: IFont) => void;
}

const Option: React.FunctionComponent<OptionProps> = ({
  value,
  selected,
  onChange
}) => {
  const comps = value.family.split(':');
  const fontFamily = comps[0];
  const fontStyle = comps.length > 1 ? comps[1] : 'normal';
  return (
    <div
      onClick={selected ? undefined : () => onChange(value)}
      className={
        'bf-select-option' + (selected ? ' bf-select-option--selected' : '')
      }
      style={{
        fontFamily,
        fontStyle,
        fontWeight: value.weight as any,
        fontSize: 20
      }}
    >
      {value.label}
    </div>
  );
};

let cachedTemplateFonts: IFont[] | undefined;

interface Props {
  name: string;
  value: IFont;
  onChange: (property: string, value: any) => void;
}

interface State {
  isOpen: boolean;
  isLoaded: boolean;
  templatesFonts?: IFont[];
}

class FontPicker extends React.Component<Props, State> {
  state: State = {
    isOpen: false,
    isLoaded: false
  };
  popover = React.createRef<HTMLDivElement>();

  async componentDidMount() {
    if (cachedTemplateFonts) {
      this.setState({
        isLoaded: true,
        templatesFonts: cachedTemplateFonts
      });
    } else {
      let { allTemplates } = getStoreState().app;
      if (!allTemplates) {
        allTemplates = await getTemplates();
        storeDispatch({
          type: Actions.DID_LOAD_TEMPLATES,
          templates: allTemplates
        });
      }
      const usedFontsMap = {};
      AVAILABLE_FONTS.forEach(f => {
        const key = `${f.family}-${f.weight}`;
        usedFontsMap[key] = true;
      });
      const fonts: IFont[] = [];
      const addFont = (f: IFont) => {
        if (isSystemFont(f)) {
          return;
        }
        const key = `${f.family}-${f.weight}`;
        if (!usedFontsMap[key]) {
          fonts.push(f);
          usedFontsMap[key] = true;
        }
      };
      allTemplates.forEach(template => {
        template.layers?.forEach(layer => {
          layer.frames.forEach(frame => {
            if (frame.type === FrameType.Text) {
              const textData = frame as TextFrameData;
              const { family, weight } = textData;
              addFont({
                label: `${family} ${getWeightString(weight)}`,
                family,
                weight
              });
            }
          });
        });
      });
      fonts.sort((a, b) => a.label.localeCompare(b.label));
      const allFonts = AVAILABLE_FONTS.concat(fonts);
      const characters = getLabelsUniqueCharacters(allFonts);
      await loadFonts(allFonts, characters);
      cachedTemplateFonts = fonts;
      this.setState({
        isLoaded: true,
        templatesFonts: fonts
      });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.isOpen !== this.state.isOpen && this.state.isOpen) {
      // positionate window
      if (this.popover.current) {
        const { left, top } = (
          ReactDOM.findDOMNode(this) as HTMLElement
        ).getBoundingClientRect();
        const el = this.popover.current;
        const box = el.getBoundingClientRect();
        el.style.left = `${left}px`;
        if (box.bottom > window.innerHeight) {
          // needs to reduce height
          const maxHeight = window.innerHeight - box.top;
          if (maxHeight < 300 && top > 300) {
            // move up
            el.style.position = 'absolute';
            el.style.left = '0px';
            el.style.top = -top + 'px';
            el.style.maxHeight = top + 'px';
          } else {
            el.style.maxHeight = maxHeight + 'px';
          }
        }
      }
    }

    if (prevState.isOpen !== this.state.isOpen) {
      if (this.state.isOpen) {
        window.addEventListener('mousedown', this.didClickOnWindow);
      } else {
        window.removeEventListener('mousedown', this.didClickOnWindow);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.didClickOnWindow);
  }

  didClickOnWindow = (e: MouseEvent) => {
    if (
      this.popover.current &&
      !this.popover.current.contains(e.target as HTMLElement)
    ) {
      this.setState({ isOpen: false });
    }
  };

  open = (e: React.MouseEvent<HTMLDivElement>) => {
    setTimeout(() => {
      this.setState({ isOpen: true });
    }, 1);
  };

  didChange = async (value: IFont) => {
    try {
      if (!isCustomFont(value.family)) {
        await loadFonts([value]);
      }
      this.props.onChange(this.props.name, value);
    } catch {
      console.error('Could not load font: ' + value.family);
    }
  };

  showUpgradeModal = () => {
    trackEvent('Clicked Upgrade', { location: 'Fonts picker' });
    storeDispatch(Actions.showBuyingOptions());
  };

  render() {
    const { isOpen, isLoaded, templatesFonts } = this.state;
    const { user } = getStoreState().app;
    const pro = isPro(user);
    const customFonts: IFont[] = (user?.customFonts ?? []).map(f => ({
      family: makeFontNameCustom(f.name),
      weight: 400,
      label: f.name
    }));
    return (
      <div
        className={generateClassName('bf-select', {
          open: isOpen,
          long: true
        })}
        onMouseDown={isOpen ? undefined : this.open}
      >
        {isLoaded ? this.props.value.label : 'Loading...'}
        {isOpen && (
          <div
            className="bf-select-popover"
            ref={this.popover}
            style={{ overflowY: 'auto' }}
          >
            {pro && customFonts.length > 0 && (
              <>
                <div className="bf-select-option-header">Yours</div>
                {customFonts.map((f, index) => (
                  <Option
                    key={index}
                    value={f}
                    selected={isFontEqualTo(f, this.props.value)}
                    onChange={this.didChange}
                  />
                ))}
              </>
            )}
            <div className="bf-select-option-header">Selected</div>
            {AVAILABLE_FONTS.map((f, index) => (
              <Option
                key={index}
                value={f}
                selected={isFontEqualTo(f, this.props.value)}
                onChange={this.didChange}
              />
            ))}
            {templatesFonts !== undefined && (
              <div className="bf-select-option-header">Others</div>
            )}
            {templatesFonts?.map((f, index) => (
              <Option
                key={`t-${index}`}
                value={f}
                selected={isFontEqualTo(f, this.props.value)}
                onChange={this.didChange}
              />
            ))}
            {!pro && (
              <UpgradeBox>
                <a onClick={this.showUpgradeModal}>Upgrade</a> to upload and use
                your own fonts.
              </UpgradeBox>
            )}
          </div>
        )}
      </div>
    );
  }
}

export { FontPicker };



// WEBPACK FOOTER //
// ./src/controller/components/FontPicker.tsx